import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { API_URLS } from '../../Services/servicioURL';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import Sidebar from '../../components/sidebar/sidebar';
import Navbar from '../../components/navbar/navbar';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

interface Tecnico {
  id: number;
  nombre: string;
  usuario: string;
}

interface Empleado {
  cveemp: string;
  nomemp: string;
}

interface PeticionType {
  folio: number;
  cveemp: string;
  fecha_hora: string;
  titulo: string;
  justificacion: string;
  sucursal: string;
  departamento: string;
  estatus: string;
  confirmado?: boolean;
  fechapromesa?: string | null;
  motivo?: string | null;
  tecnico_id?: number | null;
  tecnicoAsignado?: Tecnico | null;
  empleado?: Empleado;
}

interface ReportFilters {
  fechaInicio: string;
  fechaFin: string;
  estatus: string;
  sucursal: string;
  tecnico: string;
}

const ESTATUS_OPTIONS = [
  { label: 'Todos los estatus', value: '' },
  { label: 'PENDIENTE', value: 'PENDIENTE' },
  { label: 'TRABAJANDO', value: 'TRABAJANDO' },
  { label: 'CANCELADO', value: 'CANCELADO' },
  { label: 'LISTO', value: 'LISTO' },
  { label: 'CONFIRMADO', value: 'CONFIRMADO' }
];

const ESTATUS_COLORS: Record<string, string> = {
  PENDIENTE: '#ffc107', 
  TRABAJANDO: '#28a745', 
  CANCELADO: '#dc3545', 
  LISTO: '#17a2b8', 
  CONFIRMADO: '#0d6efd', 
  DEFAULT: '#6c757d'
};

const formatDateTime = (date: string): string => {
  try {
    const dateObj = new Date(date);
    return isNaN(dateObj.getTime()) ? '-' : dateObj.toLocaleString('es-ES', {
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  } catch { 
    return '-'; 
  }
};

// Función para comparar fechas sin problemas de zona horaria
const compareDates = (dateString1: string, dateString2: string, comparison: 'gte' | 'lte'): boolean => {
  try {
    // Convertir ambas fechas a formato YYYY-MM-DD si no lo están ya
    const normalizeDate = (dateStr: string): string => {
      if (dateStr.includes('-') && dateStr.length === 10) {
        return dateStr;
      }
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    };

    const date1 = normalizeDate(dateString1);
    const date2 = normalizeDate(dateString2);

    if (comparison === 'gte') {
      return date1 >= date2;
    } else {
      return date1 <= date2;
    }
  } catch (error) {
    console.error('Error al comparar fechas:', error);
    return false;
  }
};

const getEstatusReal = (estatus: string, confirmado: boolean): string => confirmado ? 'CONFIRMADO' : estatus;
const getEstatusColor = (estatus: string): string => ESTATUS_COLORS[estatus] || ESTATUS_COLORS.DEFAULT;

const ReportesPeticiones: React.FC = () => {
  const toast = useRef<Toast>(null);
  const dt = useRef<DataTable<PeticionType[]>>(null);

  const [peticiones, setPeticiones] = useState<PeticionType[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);

  const [filters, setFilters] = useState<ReportFilters>({
    fechaInicio: '',
    fechaFin: '',
    estatus: '',
    sucursal: '',
    tecnico: ''
  });

  const showToast = useCallback((severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  }, []);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const [peticionesRes, tecnicosRes] = await Promise.all([
        fetch(API_URLS.peticiones.obtener),
        fetch(API_URLS.peticiones.obtenerTecnicos)
      ]);

      if (peticionesRes.ok) {
        const peticionesData = await peticionesRes.json();
        setPeticiones(Array.isArray(peticionesData) ? peticionesData : []);
      }

      if (tecnicosRes.ok) {
        const tecnicosData = await tecnicosRes.json();
        setTecnicos(tecnicosData?.tecnicos || []);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      showToast('error', 'Error', 'No se pudieron cargar los datos');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { 
    cargarDatos(); 
  }, [cargarDatos]);

  const filteredPeticiones = useMemo(() => {
    return peticiones.filter(p => {
      const estatusReal = getEstatusReal(p.estatus, !!p.confirmado);
      const fechaPeticion = p.fecha_hora;
      
      if (filters.fechaInicio && !compareDates(fechaPeticion, filters.fechaInicio, 'gte')) return false;
      if (filters.fechaFin && !compareDates(fechaPeticion, filters.fechaFin, 'lte')) return false;
      
      if (filters.estatus && estatusReal !== filters.estatus) return false;
      if (filters.sucursal && p.sucursal !== filters.sucursal) return false;
      
      if (filters.tecnico) {
        if (filters.tecnico === 'sin_asignar' && p.tecnico_id) return false;
        if (filters.tecnico !== 'sin_asignar' && p.tecnico_id?.toString() !== filters.tecnico) return false;
      }

      return true;
    });
  }, [peticiones, filters]);

  const sucursalOptions = useMemo(() => [
    { label: 'Todas las sucursales', value: '' },
    ...Array.from(new Set(peticiones.map(p => p.sucursal))).filter(Boolean).map(s => ({ label: s, value: s }))
  ], [peticiones]);

  const tecnicoOptions = useMemo(() => [
    { label: 'Todos los técnicos', value: '' },
    { label: 'Sin asignar', value: 'sin_asignar' },
    ...tecnicos.map(t => ({ label: `${t.nombre} (${t.usuario})`, value: t.id.toString() }))
  ], [tecnicos]);

  const updateFilter = useCallback((updates: Partial<ReportFilters>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      fechaInicio: '',
      fechaFin: '',
      estatus: '',
      sucursal: '',
      tecnico: ''
    });
    showToast('info', 'Filtros limpiados', 'Todos los filtros han sido restablecidos');
  }, [showToast]);

  const estatusBodyTemplate = useCallback((p: PeticionType) => {
    const estatus = getEstatusReal(p.estatus, !!p.confirmado);
    return (
      <span style={{
        backgroundColor: getEstatusColor(estatus), 
        color: 'white', 
        padding: '4px 8px',
        borderRadius: '12px', 
        fontSize: '11px', 
        fontWeight: 'bold',
        textAlign: 'center', 
        display: 'inline-block', 
        minWidth: '80px'
      }}>
        {estatus}
      </span>
    );
  }, []);

  const tecnicoBodyTemplate = useCallback((rowData: PeticionType) => {
    const tecnico = rowData.tecnicoAsignado;
    const tecnicoId = rowData.tecnico_id;
    
    if (tecnico && tecnico.usuario) {
      return (
        <div style={{ fontSize: '12px' }}>
          <div style={{ fontWeight: 'bold', color: '#495057' }}>{tecnico.usuario}</div>
          <div style={{ color: '#6c757d', fontSize: '11px' }}>({tecnico.nombre || 'N/A'})</div>
        </div>
      );
    }
    
    if (tecnicoId) {
      const tecnicoEncontrado = tecnicos.find(t => t.id === tecnicoId);
      if (tecnicoEncontrado) {
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ fontWeight: 'bold', color: '#495057' }}>{tecnicoEncontrado.usuario}</div>
            <div style={{ color: '#6c757d', fontSize: '11px' }}>({tecnicoEncontrado.nombre})</div>
          </div>
        );
      }
    }
    
    return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>Sin asignar</span>;
  }, [tecnicos]);

  const exportToExcel = useCallback(async () => {
    if (filteredPeticiones.length === 0) {
      showToast('warn', 'Advertencia', 'No hay datos para exportar');
      return;
    }

    setExportingExcel(true);
    try {
      // Preparar datos para Excel
      const excelData = filteredPeticiones.map(p => {
        const estatus = getEstatusReal(p.estatus, !!p.confirmado);
        const tecnico = p.tecnicoAsignado?.nombre || 
          (p.tecnico_id ? tecnicos.find(t => t.id === p.tecnico_id)?.nombre : null) || 
          'Sin asignar';
        
        return {
          'Folio': p.folio,
          'Fecha y Hora': formatDateTime(p.fecha_hora),
          'Empleado': p.empleado?.nomemp || 'N/A',
          'Título': p.titulo,
          'Estatus': estatus,
          'Técnico Asignado': tecnico
        };
      });

      // Convertir a CSV
      const headers = Object.keys(excelData[0]);
      const csvContent = [
        headers.join(','),
        ...excelData.map(row => 
          headers.map(header => {
            const value = row[header as keyof typeof row];
            const stringValue = String(value || '');
            return stringValue.includes(',') || stringValue.includes('"') 
              ? `"${stringValue.replace(/"/g, '""')}"` 
              : stringValue;
          }).join(',')
        )
      ].join('\n');

      // Crear y descargar archivo
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `reporte_peticiones_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showToast('success', 'Éxito', 'Reporte exportado a Excel exitosamente');
    } catch (error) {
      console.error('Error al exportar Excel:', error);
      showToast('error', 'Error', 'No se pudo exportar a Excel');
    } finally {
      setExportingExcel(false);
    }
  }, [filteredPeticiones, tecnicos, showToast]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem', color: '#3b82f6' }}></i>
          <div style={{ marginTop: '1rem', color: '#6b7280' }}>Cargando datos...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <Sidebar />
      <Navbar />
      
      <div className="content-wrapper" style={{
        backgroundImage: 'url(/dist/img/FondoLogin.png)', 
        backgroundSize: 'cover',
        backgroundPosition: 'center', 
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed', 
        minHeight: '100vh', 
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ 
              margin: 0, 
              color: '#1f2937', 
              fontSize: '1.8rem', 
              fontWeight: '700'
            }}>
              📊 Reportes de Peticiones
            </h1>
            <Button 
              label="Exportar Excel" 
              icon="pi pi-file-excel" 
              onClick={exportToExcel}
              disabled={exportingExcel || filteredPeticiones.length === 0}
              loading={exportingExcel}
              style={{
                backgroundColor: '#10b981', 
                border: 'none',
                borderRadius: '6px'
              }} 
            />
          </div>
        </div>

        {/* Filtros */}
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          borderRadius: '12px', 
          padding: '20px', 
          marginBottom: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)' 
        }}>
          <h3 style={{ 
            margin: '0 0 16px 0', 
            color: '#1f2937', 
            fontSize: '1.1rem', 
            fontWeight: '600'
          }}>
            🔍 Filtros de Búsqueda
          </h3>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                📅 Fecha Inicio
              </label>
              <InputText 
                type="date" 
                value={filters.fechaInicio}
                onChange={(e) => updateFilter({ fechaInicio: e.target.value })}
                style={{
                  width: '100%',
                  height: '40px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                📅 Fecha Fin
              </label>
              <InputText 
                type="date" 
                value={filters.fechaFin}
                onChange={(e) => updateFilter({ fechaFin: e.target.value })}
                style={{
                  width: '100%',
                  height: '40px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                📋 Estatus
              </label>
              <Dropdown 
                value={filters.estatus}
                options={ESTATUS_OPTIONS}
                optionLabel="label" 
                optionValue="value"
                onChange={(e) => updateFilter({ estatus: e.value })}
                placeholder="Seleccionar estatus"
                style={{ 
                  width: '100%', 
                  height: '40px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px'
                }}
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                🏢 Sucursal
              </label>
              <Dropdown 
                value={filters.sucursal}
                options={sucursalOptions}
                optionLabel="label" 
                optionValue="value"
                onChange={(e) => updateFilter({ sucursal: e.value })}
                placeholder="Seleccionar sucursal"
                style={{ 
                  width: '100%', 
                  height: '40px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                filter 
                filterBy="label"
              />
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '0.9rem', 
                fontWeight: '600', 
                marginBottom: '6px', 
                color: '#374151' 
              }}>
                👨‍🔧 Técnico
              </label>
              <Dropdown 
                value={filters.tecnico}
                options={tecnicoOptions}
                optionLabel="label" 
                optionValue="value"
                onChange={(e) => updateFilter({ tecnico: e.value })}
                placeholder="Seleccionar técnico"
                style={{ 
                  width: '100%', 
                  height: '40px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px'
                }}
                filter 
                filterBy="label"
              />
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ color: '#6b7280', fontSize: '14px' }}>
              Registros encontrados: <strong>{filteredPeticiones.length}</strong>
            </div>
            <Button 
              label="Limpiar Filtros" 
              onClick={resetFilters} 
              className="p-button-text" 
              size="small"
              style={{ color: '#6b7280' }} 
            />
          </div>
        </div>

        {/* Tabla */}
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.95)', 
          borderRadius: '12px', 
          padding: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)' 
        }}>
          <DataTable 
            ref={dt}
            value={filteredPeticiones} 
            paginator 
            rows={25} 
            rowsPerPageOptions={[10, 25, 50]}
            loading={loading} 
            responsiveLayout="scroll" 
            stripedRows 
            style={{ fontSize: '14px' }}
            emptyMessage="No hay peticiones que coincidan con los filtros"
          >
            <Column 
              field="folio" 
              header="Folio" 
              sortable 
              style={{ width: '80px', textAlign: 'center' }} 
            />
            <Column 
              field="fecha_hora" 
              header="Fecha y Hora" 
              body={(p: PeticionType) => formatDateTime(p.fecha_hora)} 
              style={{ width: '180px', textAlign: 'center' }} 
              sortable 
            />
            <Column 
              field="empleado.nomemp" 
              header="Empleado" 
              body={(p: PeticionType) => p.empleado?.nomemp || 'N/A'} 
              sortable 
            />
            <Column 
              field="titulo" 
              header="Título" 
              sortable
              style={{ maxWidth: '300px' }}
            />
            <Column 
              field="estatus" 
              header="Estatus" 
              body={estatusBodyTemplate} 
              sortable 
              style={{ width: '120px', textAlign: 'center' }} 
            />
            <Column 
              field="tecnico" 
              header="Técnico Asignado" 
              body={tecnicoBodyTemplate} 
              style={{ width: '200px' }} 
              sortable 
            />
          </DataTable>
        </div>
      </div>
    </>
  );
};

export default ReportesPeticiones;